import React from 'react';
import { ApolloError } from '@apollo/client';
import { getApplicationError, getValidationErrors } from '@oysterjs/core/graphql/errors';

import { client } from '~/api/client';
import {
  type CaraWorkspaceFieldsFragment,
  CaraWorkspaceSchemaVersion,
  StopCaraWorkspaceDocument,
  type StopCaraWorkspaceMutation
} from '~/api/graphql';

/**
 * Owns the "stop running workspace" mutation. Only V2 workspaces support stopping;
 * older schema versions are silently no-ops to match the original behavior.
 */
export function useStopCaraWorkspace(args: {
  workspace: CaraWorkspaceFieldsFragment | undefined;
  setWorkspace: (workspace: CaraWorkspaceFieldsFragment) => void;
}) {
  const { workspace, setWorkspace } = args;
  const [stopLoading, setStopLoading] = React.useState(false);

  const stopCaraWorkspace = React.useCallback(async () => {
    if (!workspace || stopLoading) {
      return;
    }

    if (workspace.schemaVersion !== CaraWorkspaceSchemaVersion.V2) {
      return;
    }

    setStopLoading(true);
    try {
      const res = await client.mutate<StopCaraWorkspaceMutation>({
        mutation: StopCaraWorkspaceDocument,
        variables: { id: workspace.id }
      });

      const applicationError = getApplicationError(new ApolloError({ graphQLErrors: res.errors }));
      const validationErrors = getValidationErrors(new ApolloError({ graphQLErrors: res.errors }));

      if (applicationError || validationErrors) {
        return;
      }

      if (res.data?.stopCaraWorkspace) {
        setWorkspace(res.data.stopCaraWorkspace);
      }
    } catch (e) {
      // Log the error but don't crash the page
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setStopLoading(false);
    }
  }, [workspace, stopLoading, setWorkspace]);

  return { stopLoading, stopCaraWorkspace };
}
